import { Button } from "@/toggle-ui-kit/button";
import { logAiAskOpen, logAiAskQuestion } from "@/utils/usage-log";
import { useRef, useState } from "react";

import { AiChatWindow } from "./ai-chat-window";
import type { Faq } from "./ai-faq";
import type { Step } from "./steps";

/** 정해진 답변이지만 대화처럼 보이도록 기다리는 시간. 테스트가 같은 값을 쓴다 */
export const ANSWER_DELAY_MS = 1000;

export const AiAskButton = ({
  canOpenStep,
  onOpenStep,
}: {
  canOpenStep: (stepId: Step["id"]) => boolean;
  onOpenStep: (stepId: Step["id"]) => void;
}) => {
  const [open, setOpen] = useState(false);
  // 닫으면 "AI 물어보기" 로 포커스를 돌려준다. 버튼은 창이 열릴 때 사라지므로 다시 그려질 때 잡는다
  const closedByUser = useRef(false);
  // 닫았다 다시 열어도 답변이 남도록 창 밖에 둔다. 페이지를 떠나면 사라진다
  const [faq, setFaq] = useState<Faq | null>(null);
  const [answering, setAnswering] = useState(false);

  if (open) {
    return (
      <AiChatWindow
        faq={faq}
        answering={answering}
        canOpenStep={canOpenStep}
        onSelect={(next) => {
          setFaq(next);
          if (!next) return;
          logAiAskQuestion(next.question);
          setAnswering(true);
          // ponytail: 닫거나 페이지를 떠나도 타이머는 그대로 끝난다. 상태 하나를 내릴 뿐이라 정리하지 않는다
          setTimeout(() => setAnswering(false), ANSWER_DELAY_MS);
        }}
        onOpenStep={(stepId) => {
          setOpen(false);
          onOpenStep(stepId);
        }}
        onClose={() => {
          closedByUser.current = true;
          setOpen(false);
        }}
      />
    );
  }

  return (
    <div className="fixed right-10 bottom-10 z-10 rounded-full bg-[linear-gradient(101.34deg,rgba(251,100,182,0.2)_5.69%,rgba(81,162,255,0.2)_100%)] p-[2.5px] shadow-[0px_4px_8px_4px_rgba(0,0,0,0.05),0px_10px_15px_-3px_rgba(0,0,0,0.1)]">
      {/* Figma 는 3px 그라데이션 선을 테두리 중앙에 그린다. CSS border 는 안쪽에만 그려지므로 1.5px 씩 여백을 옮겨 크기를 맞춘다.
          테두리 그라데이션은 파란색이 좌우를 오간다(ai-ask-flip). 분홍 바탕에 파란 띠를 둔
          패턴(분홍→파랑→분홍)을 폭 200% 로 깔고 반 주기만 왕복시키면, 파란 띠가 한 덩어리로
          왼쪽↔오른쪽을 지나간다. 양 끝에 머물다 확 넘어가는 느낌이라 이징은 가속 곡선을 쓴다 —
          멈춰 있을 때(100% 위치)가 Figma 의 파랑→분홍 그대로다 */}
      <div className="animate-ai-ask-flip rounded-full border-[3px] border-transparent [background:linear-gradient(white,white)_0_0/100%_100%_padding-box,linear-gradient(101.34deg,#fb64b6_0%,#51a2ff_50%,#fb64b6_100%)_100%_0/200%_100%_border-box] motion-reduce:animate-none">
        {/* 테두리·그림자는 바깥 두 div 가 그린다. 버튼은 호버/프레스 오버레이만 맡는다.
            color="primary" 는 오버레이를 Decorate/Interaction/Blue 로 쓰려고 고른 값이다 — 이 버튼의
            글자는 파랑이 아니라 label-normal 이라 text 색만 다시 덮는다.
            오버레이가 알약 전체를 덮어야 해서 바깥 여백(px 6.5 / py 2.5)을 버튼 크기로 옮겼다:
            h 53 = size 48 의 h-12 + 위아래 2.5, px 18.5 = size 48 의 px-3 + 좌우 6.5.
            UI kit 의 size={48} 토큰(h-12 px-3 rounded-[8px])이 바뀌면 이 값도 다시 계산해야 한다 */}
        <Button
          color="primary"
          size={48}
          autoFocus={closedByUser.current}
          className="h-[53px] rounded-full border-0 px-[18.5px] text-label-normal"
          leftIcon={
            <img className="size-6" src="/icons/invector-ai-bot.svg" alt="" />
          }
          onClick={() => {
            logAiAskOpen();
            setOpen(true);
          }}
        >
          AI 물어보기
        </Button>
      </div>
    </div>
  );
};
